/*
  What the family navigator hands to Cytoscape: the nodes and edges with their positions, and the style sheet.
  Colour is for state only. A node's ring is its state (dashed amber for a proposal, orange waiting, teal
  validated, red-orange sent back, grey-red rejected, neutral loaded); the pie inside it is the progress
  (validated and waiting); the soft halo is the calm hue of its library.
*/

import type cytoscape from "cytoscape";

import type { Id } from "../../../api/types";
import type { Highlight } from "../model/filters";
import { isDimmed } from "../model/filters";
import { hueOf, type NavModel } from "../model/nav";
import type { TreeLayout } from "./layout";

/** The colours of the theme, read from the style sheet of the page so the light and dark themes both work. */
export interface Palette {
  text: string;
  muted: string;
  surface: string;
  border: string;
  accent: string;
  proposed: string;
  waiting: string;
  validated: string;
  sentBack: string;
  rejected: string;
  loaded: string;
  hues: string[];
}

export const DEFAULT_PALETTE: Palette = {
  text: "#1c2330",
  muted: "#566274",
  surface: "#ffffff",
  border: "#dde2ea",
  accent: "#2b6cb0",
  proposed: "#8a5a00",
  waiting: "#a8480c",
  validated: "#0b6b63",
  sentBack: "#b8321a",
  rejected: "#6f4f55",
  loaded: "#4f5a6b",
  hues: ["#4a7fb5", "#5a9a8a", "#8b7bb8", "#b0824f", "#6f8f4e", "#a8697f"],
};

/** Read the palette from the CSS variables of the page. A variable that is not set keeps its default. */
export function readPalette(root: Element | null = typeof document === "undefined" ? null : document.documentElement): Palette {
  if (!root || typeof getComputedStyle === "undefined") return DEFAULT_PALETTE;
  const style = getComputedStyle(root);
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    text: read("--text", DEFAULT_PALETTE.text),
    muted: read("--text-muted", DEFAULT_PALETTE.muted),
    surface: read("--surface", DEFAULT_PALETTE.surface),
    border: read("--border", DEFAULT_PALETTE.border),
    accent: read("--accent", DEFAULT_PALETTE.accent),
    proposed: read("--state-proposed", DEFAULT_PALETTE.proposed),
    waiting: read("--state-waiting", DEFAULT_PALETTE.waiting),
    validated: read("--state-validated", DEFAULT_PALETTE.validated),
    sentBack: read("--state-sent-back", DEFAULT_PALETTE.sentBack),
    rejected: read("--state-rejected", DEFAULT_PALETTE.rejected),
    loaded: read("--state-loaded", DEFAULT_PALETTE.loaded),
    hues: DEFAULT_PALETTE.hues.map((fallback, index) => read(`--kind-${index + 1}`, fallback)),
  };
}

export interface GraphView {
  model: NavModel;
  expanded: ReadonlySet<Id>;
  layout: TreeLayout;
  hues: Map<Id, number>;
  highlight: Highlight | null;
  selectedId: Id | null;
  palette: Palette;
}

const PERCENT = (part: number, total: number) => (total > 0 ? Math.round((Math.min(part, total) / total) * 1000) / 10 : 0);

/** The nodes and edges to draw, with their positions. */
export function buildElements(view: GraphView): cytoscape.ElementDefinition[] {
  const { model, expanded, layout, hues, highlight, selectedId, palette } = view;
  const out: cytoscape.ElementDefinition[] = [];
  const drawn = new Set(layout.order);
  for (const id of layout.order) {
    const node = model.byId.get(id);
    const at = layout.positions.get(id);
    if (!node || !at) continue;
    const children = model.childrenOf.get(id) ?? [];
    const folded = children.length > 0 && !expanded.has(id);
    const progress = model.progress.get(id) ?? null;
    const hue = hueOf(model, hues, id);
    const classes = [
      `state-${node.state ?? "none"}`,
      `level-${node.kind}`,
      isDimmed(highlight, id) ? "dim" : "",
      id === selectedId ? "chosen" : "",
      folded ? "folded" : "",
      node.abstract || !node.drawn ? "not-drawn" : "",
    ]
      .filter(Boolean)
      .join(" ");
    out.push({
      group: "nodes",
      data: {
        id,
        label: folded ? `${node.label} (+${model.below.get(id) ?? children.length})` : node.label,
        pv: progress ? PERCENT(progress.validated, progress.total) : 0,
        pw: progress ? PERCENT(progress.waiting, progress.total) : 0,
        halo: palette.hues[(hue - 1) % palette.hues.length] ?? palette.hues[0],
      },
      position: { x: at.x, y: at.y },
      classes,
    });
  }
  for (const id of layout.order) {
    const node = model.byId.get(id);
    if (!node?.parentId || !drawn.has(node.parentId)) continue;
    out.push({
      group: "edges",
      data: { id: `edge:${node.parentId}:${id}`, source: node.parentId, target: id },
      classes: [model.edgeKind.get(id) ?? "heading", isDimmed(highlight, id) ? "dim" : ""].filter(Boolean).join(" "),
    });
  }
  return out;
}

/** A style value read from the data of the node. The typings of the library want a number for a pie slice. */
const fromData = (field: string) => `data(${field})` as unknown as number;

/** The style sheet of the navigator. */
export function buildStyle(p: Palette): cytoscape.StylesheetJson {
  return [
    {
      selector: "node",
      style: {
        shape: "ellipse",
        width: 26,
        height: 26,
        label: "data(label)",
        color: p.text,
        "font-size": 12,
        "font-family": 'system-ui, "Segoe UI", Roboto, sans-serif',
        "text-valign": "center",
        "text-halign": "right",
        "text-margin-x": 10,
        "text-wrap": "ellipsis",
        "text-max-width": "150px",
        "min-zoomed-font-size": 7,
        "background-color": p.surface,
        "border-width": 3,
        "border-color": p.border,
        "border-style": "solid",
        "pie-size": "70%",
        "pie-1-background-color": p.validated,
        "pie-1-background-size": fromData("pv"),
        "pie-2-background-color": p.waiting,
        "pie-2-background-size": fromData("pw"),
        "underlay-color": "data(halo)",
        "underlay-opacity": 0.35,
        "underlay-padding": 4,
        "underlay-shape": "ellipse",
      },
    },
    { selector: "node.level-library", style: { width: 38, height: 38, "font-size": 14, "font-weight": 700 } },
    { selector: "node.level-family", style: { width: 32, height: 32, "font-weight": 600 } },
    { selector: "node.level-sub_family", style: { width: 28, height: 28 } },
    { selector: "node.folded", style: { shape: "round-rectangle" } },
    { selector: "node.not-drawn", style: { color: p.muted } },
    // The ring is the state. A proposal is dashed, whatever the colour.
    { selector: "node.state-proposed", style: { "border-color": p.proposed, "border-style": "dashed" } },
    { selector: "node.state-waiting", style: { "border-color": p.waiting } },
    { selector: "node.state-validated", style: { "border-color": p.validated } },
    { selector: "node.state-sent_back", style: { "border-color": p.sentBack, "border-style": "double", "border-width": 5 } },
    { selector: "node.state-rejected", style: { "border-color": p.rejected, "border-style": "dotted" } },
    { selector: "node.state-loaded", style: { "border-color": p.loaded, "border-width": 2 } },
    { selector: "node.chosen", style: { "overlay-color": p.accent, "overlay-opacity": 0.2, "overlay-padding": 9, "font-weight": 700 } },
    { selector: "node.dim", style: { opacity: 0.22 } },
    {
      selector: "edge",
      style: {
        width: 1.5,
        "line-color": p.border,
        "curve-style": "taxi",
        "taxi-direction": "rightward",
        "taxi-turn": "45%",
        "target-arrow-shape": "none",
      },
    },
    { selector: "edge.heading", style: { "line-style": "dotted" } },
    { selector: "edge.main_parent", style: { "line-style": "solid" } },
    { selector: "edge.dim", style: { opacity: 0.2 } },
  ];
}
